import React, { Component } from 'react'
import cn from 'classnames'
import Hammer from 'hammerjs'
import ReactDOM from 'react-dom'
import SimpleCard from './SimpleCard'
import { translate3d } from './utils'
import s from './styles.module.scss'
import { ENTER_KEY_CODE, LEFT_ARROW_KEY_CODE, RIGHT_ARROW_KEY_CODE } from 'constants/common'

const animations = {
  skip: s.swipeOutLeft,
  like: s.swipeOutRight
}

class DraggableCard extends Component {
  state = {
    x: 0,
    y: 0,
    initialPosition: { x: 0, y: 0 },
    startPosition: { x: 0, y: 0 },
    pristine: true,
    swipeOut: null,
    useCssAnimation: false
  }
  timeout = null
  isMoving = false
  isMouseDown = false
  mouseStartX = 0
  mouseStartY = 0
  // Flag to prevent duplicate API calls when animate() is triggered programmatically (e.g., button click)
  isAnimatingProgrammatically = false
  // Flag to track when Hammer.js is handling a pan gesture (to prevent duplicate mouse handler processing)
  isPanningWithHammer = false

  resetPosition = () => {
    const { x, y } = this.props.containerSize
    const card = ReactDOM.findDOMNode(this)
    const initialPosition = {
      x: Math.round((x - card.offsetWidth) / 2),
      y: Math.round((y - card.offsetHeight) / 2)
    }

    this.setState(state => ({
      ...state,
      x: initialPosition.x,
      y: 2,
      initialPosition: initialPosition,
      startPosition: { x: 0, y: 0 }
    }))
  }

  panstart = () => {
    // Mark that Hammer is handling this gesture to prevent duplicate mouse handler processing
    this.isPanningWithHammer = true
    const { x, y } = this.state
    this.setState(state => ({
      ...state,
      startPosition: { x, y },
      pristine: false
    }))
  }

  getDirection = () => {
    const { containerSize: screen } = this.props
    const card = ReactDOM.findDOMNode(this)
    const { x, y } = this.state
    switch (true) {
      case (x < -120): return 'Left'
      case (x + (card.offsetWidth - 120) > screen.x): return 'Right'
      case (y < -120): return 'Top'
      case (y + (card.offsetHeight - 120) > screen.y): return 'Bottom'
      default: return false
    }
  }

  panend = (ev) => {
    // Don't process pan end if a programmatic animation is in progress
    if (this.isAnimatingProgrammatically) return

    const direction = this.getDirection()
    ev.preventDefault()
    setTimeout(() => {
      this.isMoving = false
      // Reset Hammer panning flag after gesture completes
      this.isPanningWithHammer = false
    }, 0)
    if (this.props[`onSwipe${direction}`]) {
      // Trigger swipe handler (like/skip)
      this.props[`onSwipe${direction}`]()

      // Animate card out, then notify parent to remove after animation completes
      const type = direction === 'Left' ? 'skip' : direction === 'Right' ? 'like' : null
      if (type) {
        // Continue animation from current position to off-screen
        const targetX = type === 'skip' ? -600 : 600
        const targetY = this.state.y - 100
        this.setState({ swipeOut: type, x: targetX, y: targetY, animation: true })
        this.timeout && clearTimeout(this.timeout)
        this.timeout = setTimeout(this.props[`onOutScreen${direction}`], 400)
      } else {
        this.resetPosition()
        this.setState(state => ({ ...state, animation: true }))
      }
    } else {
      this.resetPosition()
      this.setState(state => ({ ...state, animation: true }))
    }
  }

  panmove(ev) {
    this.isMoving = true
    this.setState({
      ...this.calculatePosition(ev.deltaX, ev.deltaY),
      animation: false
    })
  }

  handlePan = (ev) => {
    ev.preventDefault()
    this[ev.type](ev)
    return false
  }

  calculatePosition(deltaX, deltaY) {
    const { initialPosition: { x, y } } = this.state
    return {
      x: (x + deltaX),
      y: (y + deltaY)
    }
  }

  animate = (type) => {
    if (type !== this.state.type) {
      // Mark that we're animating programmatically to prevent duplicate API calls
      this.isAnimatingProgrammatically = true
      // Reset mouse state to prevent onMouseUp from triggering swipe logic
      this.isMouseDown = false
      // Button-triggered swipe should use CSS animation
      this.setState({ swipeOut: type, x: type === 'skip' ? -300 : 300, useCssAnimation: true })
      this.timeout = setTimeout(() => {
        this.isAnimatingProgrammatically = false
        this.props[`onOutScreen${type === 'skip' ? 'Left' : 'Right'}`]()
      }, 400)
    }
  }

  componentDidMount() {
    const { onNewCardAppear, item } = this.props
    onNewCardAppear(item, this.animate)
    this.hammer = new Hammer.Manager(ReactDOM.findDOMNode(this))
    this.hammer.add(new Hammer.Pan({ threshold: 20 }))
    this.hammer.on('panstart panend panmove', this.handlePan)

    // Position card - CSS animation handles the entry animation
    this.resetPosition()

    window.addEventListener('resize', this.resetPosition)

    // Add mouse event listeners for click and hold functionality
    const cardElement = ReactDOM.findDOMNode(this)
    if (cardElement) {
      cardElement.addEventListener('mousedown', this.onMouseDown)
      document.addEventListener('mousemove', this.onMouseMove)
      document.addEventListener('mouseup', this.onMouseUp)
      cardElement.addEventListener('mouseleave', this.onMouseLeave)
    }
  }

  componentWillUnmount() {
    if (this.hammer) {
      this.hammer.stop()
      this.hammer.destroy()
      this.hammer = null
    }
    window.removeEventListener('resize', this.resetPosition)
    this.timeout && clearTimeout(this.timeout)

    // Remove mouse event listeners
    const cardElement = ReactDOM.findDOMNode(this)
    if (cardElement) {
      cardElement.removeEventListener('mousedown', this.onMouseDown)
      document.removeEventListener('mousemove', this.onMouseMove)
      document.removeEventListener('mouseup', this.onMouseUp)
      cardElement.removeEventListener('mouseleave', this.onMouseLeave)
    }
  }

  onClick = () => this.props.onClick(this.isMoving)

  onKeyDown = (event) => {
    switch (event.keyCode) {
      case LEFT_ARROW_KEY_CODE:
        if (this.props.onSwipeLeft) {
          event.preventDefault();
          this.props.onSwipeLeft();
          // Keyboard-triggered swipe should use CSS animation
          this.setState({ swipeOut: 'skip', x: -300, useCssAnimation: true });
          this.timeout && clearTimeout(this.timeout);
          this.timeout = setTimeout(this.props.onOutScreenLeft, 400);
        }
        break;
      case RIGHT_ARROW_KEY_CODE:
        if (this.props.onSwipeRight) {
          event.preventDefault();
          this.props.onSwipeRight();
          // Keyboard-triggered swipe should use CSS animation
          this.setState({ swipeOut: 'like', x: 300, useCssAnimation: true });
          this.timeout && clearTimeout(this.timeout);
          this.timeout = setTimeout(this.props.onOutScreenRight, 400);
        }
        break;
      case ENTER_KEY_CODE:
        if (event.target.tagName === "ARTICLE") {
          this.onClick()
        }
        break;
      default:
        break
    }
  }

  onMouseDown = (event) => {
    // Only handle left mouse button
    if (event.button !== 0) return

    this.isMouseDown = true
    this.mouseStartX = event.clientX
    this.mouseStartY = event.clientY

    const { x, y } = this.state
    this.setState(state => ({
      ...state,
      startPosition: { x, y },
      pristine: false
    }))
  }

  onMouseMove = (event) => {
    if (!this.isMouseDown) return

    event.preventDefault()
    this.isMoving = true

    const deltaX = event.clientX - this.mouseStartX
    const deltaY = event.clientY - this.mouseStartY

    this.setState({
      ...this.calculatePosition(deltaX, deltaY),
      animation: false
    })
  }

  onMouseUp = (event) => {
    if (!this.isMouseDown) return
    // Don't process mouse up if a programmatic animation is in progress
    if (this.isAnimatingProgrammatically) {
      this.isMouseDown = false
      return
    }
    // Don't process mouse up if Hammer.js is already handling this gesture
    // (prevents duplicate API calls when both systems fire for mouse events)
    if (this.isPanningWithHammer) {
      this.isMouseDown = false
      return
    }

    this.isMouseDown = false
    const direction = this.getDirection()

    setTimeout(() => { this.isMoving = false }, 0)

    if (this.props[`onSwipe${direction}`]) {
      // Trigger swipe handler (like/skip)
      this.props[`onSwipe${direction}`]()

      // Animate card out, then notify parent to remove after animation completes
      const type = direction === 'Left' ? 'skip' : direction === 'Right' ? 'like' : null
      if (type) {
        // Continue animation from current position to off-screen
        const targetX = type === 'skip' ? -600 : 600
        const targetY = this.state.y - 100
        this.setState({ swipeOut: type, x: targetX, y: targetY, animation: true })
        this.timeout && clearTimeout(this.timeout)
        this.timeout = setTimeout(this.props[`onOutScreen${direction}`], 400)
      } else {
        this.resetPosition()
        this.setState(state => ({ ...state, animation: true }))
      }
    } else {
      this.resetPosition()
      this.setState(state => ({ ...state, animation: true }))
    }
  }

  onMouseLeave = (event) => {
    if (!this.isMouseDown) return

    // Treat mouse leave as mouse up
    this.onMouseUp(event)
  }

  render() {
    const { x, y, swipeOut, animation, useCssAnimation } = this.state
    const [style, likeStyle, skipStyle] = translate3d(x, y)

    return <SimpleCard
      {...this.props}
      style={style}
      likeStyle={likeStyle}
      skipStyle={skipStyle}
      onClick={this.onClick}
      onKeyDown={this.onKeyDown}
      className={cn(
        this.props.className,
        swipeOut && useCssAnimation && animations[swipeOut],
        animation && s.onCardBack
      )}
    />
  }
}

export default DraggableCard
